import { CircleAlert, Info, TriangleAlert } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getLabelBadgeStyle } from "@/lib/semanticColors";
import { cn } from "@/lib/utils";

/**
 * The three icon names the API sends on a label. Anything else renders as a
 * plain pill rather than a broken glyph.
 */
const LABEL_ICONS = {
  error: CircleAlert,
  warning: TriangleAlert,
  info: Info,
};

function LabelPill({ label, className }) {
  const Icon = LABEL_ICONS[label.icon];
  const style = getLabelBadgeStyle(label.color);

  const body = (
    <>
      {Icon && <Icon className="size-3 shrink-0" aria-hidden="true" />}
      <span className="truncate">{label.text}</span>
    </>
  );

  // A label with a redirect path is a navigation control, so it is a real link —
  // the old app put a click handler on a `span` with `role="link"`, which gave
  // no keyboard activation and no middle-click.
  if (label.redirectPath) {
    return (
      <Link
        to={label.redirectPath}
        onClick={(event) => event.stopPropagation()}
        style={style}
        className={cn("invoice-label invoice-label-link", className)}
        title={`Go to ${label.text}`}
      >
        {body}
      </Link>
    );
  }

  return (
    <span style={style} className={cn("invoice-label", className)} title={label.text}>
      {body}
    </span>
  );
}

/**
 * Renders the labels attached to an invoice. Past `maxVisible`, the remainder
 * collapse into a `+N` chip that lists them all on hover or focus.
 */
export function InvoiceLabels({ labels = [], maxVisible, className }) {
  if (labels.length === 0) return null;

  const collapse = typeof maxVisible === "number" && labels.length > maxVisible;
  const visible = collapse ? labels.slice(0, maxVisible) : labels;
  const hidden = collapse ? labels.slice(maxVisible) : [];

  return (
    <div className={cn("invoice-labels", className)}>
      {visible.map((label, index) => (
        <LabelPill key={`${label.text}-${index}`} label={label} />
      ))}

      {hidden.length > 0 && (
        <Tooltip>
          <TooltipTrigger
            render={<span className="invoice-label invoice-label-more num" tabIndex={0} />}
          >
            +{hidden.length}
          </TooltipTrigger>
          <TooltipContent className="max-w-64 flex-col items-start gap-1.5 p-2">
            {hidden.map((label, index) => (
              <LabelPill key={`${label.text}-${index}`} label={label} className="max-w-none" />
            ))}
          </TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}
